/**
 * Submission tracking.
 *
 * The storefront already runs GTM (GTM-TM2RTJ65) with GA4 and Google Ads
 * behind it, so the honest integration is a single dataLayer push: GTM owns
 * which tags fire, and we never hardcode a measurement ID that would rot the
 * day an account changes.
 *
 * One event for every form, with `form` naming the slug, so a new form is
 * tracked the moment it exists rather than needing its own wiring.
 *
 * Everything here is best-effort and swallowed: analytics must never be able
 * to break an order that has already been accepted by the server.
 */
import type { Answers, FormDefinition } from './types';
import { allFields } from './logic';

/** GTM's queue. Present on the storefront; absent standalone, which is fine. */
declare global {
  interface Window {
    dataLayer?: unknown[];
    fbq?: (...args: unknown[]) => void;
  }
}

export const SUBMIT_EVENT = 'rpl_form_submit';

function answerFor(def: FormDefinition, answers: Answers, wire: string): string {
  const field = allFields(def).find((f) => f.wire === wire);
  const v = field ? answers[field.id] : undefined;
  if (Array.isArray(v)) return v.join(', ');
  return typeof v === 'string' ? v : '';
}

/**
 * Push the submission to the dataLayer, and re-fire the Meta custom event the
 * storefront has been listening for since the Formstack days.
 *
 * @param batches how many service batches the order carried (1 = single service)
 */
export function trackSubmission(
  def: FormDefinition,
  reference: string,
  answers: Answers,
  batches: number,
): void {
  try {
    const service = answerFor(def, answers, 'Services Selection');
    const detail = {
      form: def.slug,
      reference,
      // Order shape, useful for segmenting conversions without PII. Empty
      // strings for forms that don't ask these, rather than undefined keys.
      service,
      method: answerFor(def, answers, 'How Will You Submit Your Order?'),
      speed: answerFor(def, answers, 'Speed of Service'),
      batches,
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: SUBMIT_EVENT, ...detail });

    // The theme binds fbq('trackCustom', 'MailFormSubmit') to the FORMSTACK
    // submit button id, which no longer exists on the page, so that signal has
    // been dead since the cutover. Fire it from the one place that actually
    // knows a submission succeeded.
    if (def.slug === 'order-form' && typeof window.fbq === 'function') {
      window.fbq('trackCustom', 'MailFormSubmit', { service, batches });
    }
  } catch {
    // An order is already placed by this point; never surface a tracking fault.
  }
}
